import useTranslation from "../../client/hooks/useTranslation";
import PartnerLogo from "../../client/components/legal/PartnerLogo";

/**
 * Section 2 — Social proof bar (stats + compatible prop firms).
 *
 * Sits directly under the hero on the dark-locked landing. Stats render as real
 * HTML text (SEO); prop firms scroll in a continuous marquee, as their full
 * wordmark when one exists (white silhouette), else as icon + name. Full-bleed
 * so it spans the viewport like the neighbouring sections.
 *
 * The partner row shows the NinjaTrader and Tradovate wordmarks (NinjaTrader
 * Vendor Program). NinjaTrader MUST go through <PartnerLogo>: 18px clear-space
 * and the tracked partner URL are compliance requirements. Because this page
 * now mentions NinjaTrader, App.tsx appends the trademark notice to the
 * landing footer.
 *
 * Both label rows ("official ecosystem member", "compatible with") sit ABOVE their logos,
 * centred, on every breakpoint, so the two blocks read the same way.
 */

// firm display name → file slug of its logo.
const PROP_FIRM_SLUGS: Record<string, string> = {
  FTMO: "ftmo",
  Apex: "apex",
  TopStep: "topstep",
  "Lucid Trading": "lucid",
  FundedNext: "fundednext",
  MyFundedFutures: "myfundedfutures",
  TakeProfitTrader: "takeprofittrader",
  Tradeify: "tradeify",
  Phidias: "phidias",
  "Funded Futures Family": "fff",
};

// Logos discovered at build time in src/landing-page/assets/propfirm-wordmarks/.
// They are GENERATED by scripts/normalize-propfirm-wordmarks.mjs from the raw
// files in `_source/`: white silhouette, tightly cropped, sized for equal
// visual weight on a shared 36px canvas. Never drop a raw logo here directly.
//   <slug>.png       full wordmark, shown alone
//   <slug>.mark.png  symbol only, shown with the firm name as text
//   (no file)        firm name as text only
const LOGO_FILES = import.meta.glob<string>(
  "../assets/propfirm-wordmarks/*.png",
  { eager: true, query: "?url", import: "default" },
);
const WORDMARKS: Record<string, string> = {};
const MARKS: Record<string, string> = {};
for (const [file, url] of Object.entries(LOGO_FILES)) {
  const name = file.split("/").pop()!;
  if (name.endsWith(".mark.png")) MARKS[name.slice(0, -".mark.png".length)] = url;
  else WORDMARKS[name.slice(0, -".png".length)] = url;
}

function PropFirmMark({ firm }: { firm: string }) {
  const slug = PROP_FIRM_SLUGS[firm];
  const wordmark = WORDMARKS[slug];
  if (wordmark) {
    return <img src={wordmark} alt={firm} loading="lazy" className="h-9 w-auto" />;
  }
  const mark = MARKS[slug];
  return (
    <span className="flex h-9 items-center gap-2.5">
      {mark && <img src={mark} alt="" loading="lazy" className="h-[26px] w-auto" />}
      <span className="whitespace-nowrap font-display text-[17px] font-semibold tracking-tight text-white">
        {firm}
      </span>
    </span>
  );
}

function PropFirmList({ firms, hidden }: { firms: string[]; hidden?: boolean }) {
  // pr-14 equals the gap: the seam between the two copies keeps the same rhythm,
  // so translating the track by exactly -50% loops without a jump.
  return (
    <ul
      aria-hidden={hidden || undefined}
      className={
        hidden
          ? "flex shrink-0 items-center gap-14 pr-14 motion-reduce:hidden"
          : "flex shrink-0 items-center gap-14 pr-14 motion-reduce:flex-wrap motion-reduce:justify-center motion-reduce:gap-x-10 motion-reduce:gap-y-5 motion-reduce:pr-0"
      }
    >
      {firms.map((firm) => (
        <li
          key={firm}
          className="flex items-center opacity-70 transition-opacity duration-300 hover:opacity-100"
        >
          <PropFirmMark firm={firm} />
        </li>
      ))}
    </ul>
  );
}

export default function SocialProofBar() {
  const t = useTranslation();
  const sp = t.marketing.socialProof;

  return (
    <section
      className="relative mx-[calc(-50vw+50%)] w-screen border-t border-white/10 bg-background-subtle px-4 py-8 sm:px-6 sm:py-12"
      aria-label={sp.compatibleLabel}
    >
      <div className="mx-auto max-w-[1200px]">
        {/* ── Bloc 1: stats ── */}
        <div className="grid grid-cols-2 gap-6 md:flex md:flex-wrap md:items-center md:justify-center md:gap-y-4 md:gap-x-0">
          {sp.stats.map((stat, i) => (
            <div key={stat.label} className="flex items-center md:contents">
              <div className="flex flex-1 flex-col items-center gap-1 md:flex-none md:flex-row md:items-baseline md:gap-2">
                <span className="font-display text-2xl font-extrabold leading-none text-foreground lg:text-[32px]">
                  {stat.value}
                </span>
                <span className="text-center text-xs text-muted-foreground md:max-w-[120px] md:text-left md:text-[13px] lg:max-w-none lg:text-sm">
                  {stat.label}
                </span>
              </div>
              {/* Vertical divider between stats (tablet+; gap widens on desktop) */}
              {i < sp.stats.length - 1 && (
                <span
                  aria-hidden="true"
                  className="mx-3 hidden h-10 w-px bg-white/10 md:block lg:mx-6"
                />
              )}
            </div>
          ))}
        </div>

        {/* Horizontal separator */}
        <div aria-hidden="true" className="my-8 h-px w-full bg-white/10" />

        {/* ── Bloc 2: NinjaTrader Vendor Program partners ── */}
        <div className="flex flex-col items-center gap-4">
          <p className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/60">
            {sp.partnerLabel}
          </p>
          {/* -my-[18px] absorbs PartnerLogo's mandatory vertical clear-space so the
              row's visual height matches the Tradovate mark; the 18px still
              exists around the NinjaTrader wordmark itself. */}
          <div className="-my-[18px] flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            <PartnerLogo
              partner="ninjatrader"
              src="/logos/ninjatrader-wordmark.svg"
              alt="NinjaTrader"
              imgClassName="h-6 w-auto object-contain"
            />
            <img
              src="/logos/tradovate.png"
              alt="Tradovate"
              loading="lazy"
              className="h-8 w-auto object-contain"
            />
          </div>
        </div>

        {/* Horizontal separator */}
        <div aria-hidden="true" className="my-8 h-px w-full bg-white/10" />

        {/* ── Bloc 3: compatible prop firms ── */}
        <div className="flex flex-col items-center gap-4">
          <p className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/60">
            {sp.compatibleLabel}
          </p>
          {/* Continuous marquee: two identical lists, the track slides by one list
              width. ~35px/s keeps names readable; hover pauses it; edges fade
              out via a mask. Reduced motion: a static, wrapped list. */}
          <div className="w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_12%,black_88%,transparent)] motion-reduce:[mask-image:none]">
            <div className="flex w-max animate-ticker py-1 [animation-duration:55s] hover:[animation-play-state:paused] motion-reduce:w-full motion-reduce:animate-none motion-reduce:justify-center">
              <PropFirmList firms={sp.propFirms} />
              <PropFirmList firms={sp.propFirms} hidden />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
